import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { BookOpen, MessageSquare } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

export function DashboardHeader() {
  const { user } = useAuth();
  const displayName = user?.email?.split('@')[0] || 'ผู้ใช้';
  const avatarInitial = displayName[0].toUpperCase();
  const avatarUrl = user?.user_metadata?.avatar_url ?? undefined;
  const [showFeedbackHint, setShowFeedbackHint] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('quran-assistant-feedback-menu-clicked');
      setShowFeedbackHint(stored !== '1');
    } catch {
      setShowFeedbackHint(true);
    }
  }, []);

  const handleFeedbackClick = () => {
    try {
      localStorage.setItem('quran-assistant-feedback-menu-clicked', '1');
    } catch {
      // ignore
    }
    setShowFeedbackHint(false);
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50',
        // Glassmorphism effect
        'bg-background/70 backdrop-blur-xl',
        'border-b border-border/50',
        'shadow-lg shadow-black/5',
        'flex items-center justify-between',
        'h-14 px-4',
        'transition-all duration-300'
      )}
      role="banner"
    >
      {/* Profile Avatar (left) */}
      <Link
        to="/profile"
        className="flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
        aria-label="ไปยังโปรไฟล์"
      >
        <Avatar className="h-9 w-9">
          <AvatarImage src={avatarUrl} alt={displayName} />
          <AvatarFallback className="bg-primary/20 text-primary">
            {avatarInitial}
          </AvatarFallback>
        </Avatar>
      </Link>

      {/* App name (center) */}
      <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
        <BookOpen className="h-5 w-5 text-primary" aria-hidden />
        <span className="font-semibold text-foreground">
          Quran Assistant
        </span>
      </div>

      {/* Feedback menu (right) */}
      <Link
        to="/feedback"
        onClick={handleFeedbackClick}
        className={cn(
          'relative flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium',
          'text-primary hover:text-primary-foreground',
          'bg-primary/10 hover:bg-primary',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background'
        )}
        aria-label="ไปยังแบบประเมินความพึงพอใจ"
      >
        <MessageSquare className="h-4 w-4" />
        {showFeedbackHint && (
          <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-2 py-0.5 text-[10px] animate-pulse">
            ใหม่
          </span>
        )}
      </Link>
    </header>
  );
}

export default DashboardHeader;
